import React, { useMemo } from 'react';
import FailedChecksSummaryList from './FailedChecksSummaryList';
import { useNavigate } from '../../router/hooks/useNavigate';
import useBatchUrlAnalysisProcessesPolling from '../process/hooks/useBatchUrlAnalysisProcessesPolling';
import { DPA_DASHBOARD_POLLING_TIME_MS } from '../settings';
import { useFetchOnPageSeoFailedChecksQuery } from './service';

type Props = {
    websiteGuid: string;
};

export default function UrlFailedChecks({ websiteGuid }: Props) {
    const navigate = useNavigate();

    const { isRunningBatchAnalysis } = useBatchUrlAnalysisProcessesPolling(websiteGuid);
    const { data: failedChecks, isLoading: isLoadingFailedChecks } = useFetchOnPageSeoFailedChecksQuery(
        { w_guid: websiteGuid },
        {
            // The polling interval is in milliseconds!
            pollingInterval: isRunningBatchAnalysis ? DPA_DASHBOARD_POLLING_TIME_MS : 0,
            // The polling interval is reset if we call the hook again, refetchOnMount
            // Can take a number that will refetch if the cache value is older than the value
            // This is in seconds!
            refetchOnMountOrArgChange: isRunningBatchAnalysis ? DPA_DASHBOARD_POLLING_TIME_MS / 1000 : false,
        }
    );

    const handleFailedCheckButtonClick = (seoTestKey: string) => {
        navigate(`/deep-analysis/failed-check/${seoTestKey}?w_guid=${websiteGuid}`);
    };

    const filteredChecks = useMemo(
        () => (failedChecks && failedChecks.length > 0 ? [...failedChecks].sort((a, b) => b.relevance - a.relevance) : []),
        [failedChecks]
    );

    const lastFoundDate =
        failedChecks && failedChecks.length > 0 ? [...failedChecks].sort((a, b) => b.last_found.localeCompare(a.last_found))[0].last_found : null;

    return (
        <FailedChecksSummaryList
            title="URL failed checks"
            type="url"
            failedChecks={filteredChecks}
            lastFoundDate={lastFoundDate}
            isLoading={isLoadingFailedChecks}
            isRunningBatchAnalysis={isRunningBatchAnalysis}
            onFailedCheckButtonClicked={handleFailedCheckButtonClick}
        />
    );
}
